import * as yup from "yup";
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoDocumentOutline, IoSaveOutline } from "react-icons/io5";
import { Document, Page, pdfjs } from "react-pdf";
import { getBase64, tinFormat } from "../../../utils/stringUtils";

import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
} from "../../../utils/constants";

import InnerImageZoom from "react-inner-image-zoom";
import Textbox from "../../../components/common/Textbox";
import TextboxAppend from "../../../components/common/TextboxAppend";
import Textarea from "../../../components/common/Textarea";
import Datepicker from "../../../components/common/Datepicker";
import ReactSelect from "../../../components/common/ReactSelect";
import DropdownInput from "./../../../components/common/DropdownInput";
import FileUpload from "../../../components/common/FileUpload";
import Toaster from "./../../../components/common/Toaster";
import TextboxMask from "../../../components/common/TextboxMask";

import taxService from "./../../../services/taxService";
import accountService from "../../../services/accountService";
import moment from "moment";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const schema = yup.object({
  gross_amount: yup.string().required("Please enter gross amount."),
  tax_rate: yup.string().required("Please enter tax rate."),
  client_name: yup.string().required("Please enter client name.").nullable(),
  address: yup.string().required("Please enter address.").nullable(),
  atc: yup.string().required("Please enter ATC.").nullable(),
  tin: yup.string().required("Please enter TIN.").nullable(),
  applicable_period: yup.string().required("Please select applicable period."),
  quarter: yup.object().shape({
    label: yup.string().required("Please select quarter."),
    value: yup.string().required("Please select quarter."),
  }),
  file: yup.mixed().when("id", {
    is: (value) => !value,
    then: yup.mixed().required("Please select image."),
    otherwise: yup.mixed(),
  }),
});

const CreditableForm = (props) => {
  const { id, data, isLoading, setLoading, setShowForm, onSuccess } = props;

  const [clientName, setClientName] = useState("");
  const [clients, setClients] = useState([]);
  const [params, setParams] = useState({
    page: 1,
    size: 10,
    query: "",
  });

  const [tin, setTin] = useState(null);
  const [grossAmount, setGrossAmount] = useState(0);
  const [withheldAmount, setWithheldAmount] = useState(0);
  const [netAmount, setNetAmount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);

  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);

  const [applicablePeriod, setApplicablePeriod] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const quarters = taxService.getAltTaxQuarters();

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const handleGrossChange = (e) => {
    setGrossAmount(e.target.value);
    handleCalculate(e.target.value, taxRate);
  };

  const handleTaxRateChange = (e) => {
    setTaxRate(e.target.value);
    handleCalculate(grossAmount, e.target.value);
  };

  const handleCalculate = (gross, rate) => {
    const withheld = (gross * (rate / 100)).toFixed(2);
    const net = (gross - withheld).toFixed(2);

    setWithheldAmount(withheld);
    setNetAmount(net);

    setValue("withheld_amount", withheld);
    setValue("net_amount", net);
  };

  const fetchClients = useCallback(
    async (mounted) => {
      try {
        const response = await accountService.getAccountClientsAsync(
          id,
          params
        );
        const { result } = response;

        const items = result.map((item) => {
          item["name"] = item.client_name;
          item["description"] = item.tin;

          return item;
        });

        if (mounted) setClients(items);
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [params, id]
  );

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };
      item["account_id"] = id;
      item["quarter"] = info.quarter.value;
      item["quarter_name"] = info.quarter.label;

      let document_name;

      if (!data) {
        const base = await getBase64(item.file[0]);
        const content = base.split(",")[1];
        const extension = item.file[0].path.split(".").pop();
        const file_name = `${moment().format("YYYYMMDDmss")}${id}.${extension}`;
        const file = { file_name, content };

        document_name = file_name;

        item["file"] = file;
      }

      const result = await taxService.saveAccountCreditableAsync(item);

      item["id"] = result.id;

      item["gross_value"] = info.gross_amount;
      item["withheld_value"] = info.withheld_amount;
      item["net_value"] = info.net_amount;

      item["gross_amount"] = Number(info.gross_amount).toLocaleString();
      item["withheld_amount"] = Number(info.withheld_amount).toLocaleString();
      item["net_amount"] = Number(info.net_amount).toLocaleString();

      item["document_name"] = data ? data.document_name : document_name;
      item["created_on"] = data
        ? moment(data.created_on).format("LL")
        : moment().format("LL");

      item["s3_reference_name"] = data
        ? data.s3_reference_name
        : result.s3_reference_name;

      Toaster.Success("2307 Saved Successfully!");
      onSuccess(item);
      setShowForm(false);
      setLoading(false);
    } catch (ex) {
      setLoading(false);
      Toaster.Error("Something went wrong.");
    }
  };

  const handleSelect = (item) => {
    setValue("tin", item.tin);
    setValue("address", item.address);

    setTin(item.tin);
    setClientName(item.name);

    clearErrors("tin");
    clearErrors("address");
  };

  useEffect(() => {
    if (data) {
      setValue("id", data.id);
      setValue("tax_rate", data.tax_rate);
      setValue("gross_amount", data.gross_value);
      setValue("withheld_amount", data.withheld_value);
      setValue("net_amount", data.net_value);
      setValue("tin", data.tin);
      setValue("atc", data.atc);
      setValue("client_name", data.client_name);
      setValue("address", data.address);

      setTin(data.tin);
      setClientName(data.client_name);
      setTaxRate(data.tax_rate);
      setGrossAmount(data.gross_value);
      setWithheldAmount(data.withheld_value);
      setNetAmount(data.net_value);

      if (data.client_name) {
        const applicable_period = moment(data.applicable_period).format(
          "MM-DD-yyyy"
        );

        const selected_quarter = {
          value: data.quarter,
          label: data.quarter_name,
        };

        setValue("applicable_period", applicable_period);
        setValue("quarter", selected_quarter);
        setApplicablePeriod(applicable_period);
      }
    }
  }, [data, setValue]);

  useEffect(() => {
    let mounted = true;

    fetchClients(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchClients]);

  const onDocumentLoad = ({ numPages }) => {
    setNumPages(numPages);
  };

  const renderFile = (url) => {
    const extension = url.split(".").pop().toLowerCase();

    if (extension === "pdf") {
      return (
        <>
          <Document file={{ url }} onLoadSuccess={onDocumentLoad}>
            <Page pageNumber={page} width={500} />
          </Document>
          <div className="flex justify-center text-center mb-3 text-gray-500">
            <button
              className="px-4 py-1 border rounded-lg"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              type="button"
              aria-label="Previous"
            >
              ‹
            </button>
            <span className="px-3 mt-2 text-sm">
              {page} / {numPages}
            </span>
            <button
              className="px-4 py-1 border rounded-lg"
              disabled={page >= numPages}
              onClick={() => setPage(page + 1)}
              type="button"
              aria-label="Next"
            >
              ›
            </button>
            <a
              className="button border w-12 ml-2 hover:border-blue-400"
              href={url}
              target="_blank"
              rel="noreferrer"
              title="Download File"
              download
            >
              <IoDocumentOutline className="text-lg" />
            </a>
          </div>
        </>
      );
    }

    return <InnerImageZoom src={url} zoomSrc={url} hideHint={true} />;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-16">
        {data && (
          <div className="col-span-1">
            <div className="border">{renderFile(data.s3_reference_name)}</div>
          </div>
        )}
        <div className={`space-y-6 ${!data ? "col-span-2" : "col-span-1"}`}>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              {!data && (
                <FileUpload
                  id="file-dropdown"
                  name="file"
                  title="Document"
                  setValue={setValue}
                  clearErrors={clearErrors}
                  register={register("file")}
                  accepts={MULTIMEDIA_MIME_TYPES}
                  placeholder={MULTIMEDIA_PLACEHOLDER}
                  maxFiles={1}
                  maxSize={5242880}
                  multiple={false}
                  errors={errors}
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-5">
            <div className="col-span-2">
              <Textbox
                id="gross-textbox"
                name="gross_amount"
                type="number"
                step=".01"
                title="Gross Amount"
                placeholder="0.00"
                register={register("gross_amount", {
                  onChange: (e) => handleGrossChange(e),
                })}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <TextboxAppend
                id="tax-rate-textbox"
                title="Tax Rate"
                type="number"
                name="tax_rate"
                append={<span className="text-gray-400">%</span>}
                register={register("tax_rate", {
                  onChange: (e) => handleTaxRateChange(e),
                })}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Textbox
                id="withheld-textbox"
                name="withheld_amount"
                type="text"
                title="Withheld Amount"
                value={withheldAmount}
                register={register("withheld_amount")}
                disabled
              />
            </div>
            <div className="col-span-1">
              <Textbox
                id="net-textbox"
                name="net_amount"
                type="text"
                title="Net Amount"
                value={netAmount}
                register={register("net_amount")}
                disabled
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <DropdownInput
                id="client-name-combobox"
                name="client_name"
                type="text"
                label="Client Name"
                register={register("client_name")}
                errors={errors}
                options={clients}
                params={params}
                defaultValue={clientName}
                setValue={setValue}
                clearErrors={clearErrors}
                onChange={setParams}
                onSelect={handleSelect}
              />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-6">
            <div className="col-span-3">
              <TextboxMask
                id="tin-textbox"
                name="tin"
                type="text"
                title="TIN"
                format={tinFormat}
                setValue={setValue}
                value={tin}
                register={register("tin")}
                errors={errors}
              />
            </div>
            <div className="col-span-2">
              <Textbox
                id="atc-textbox"
                name="atc"
                type="text"
                title="ATC"
                register={register("atc")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Datepicker
                id="applicable-period"
                name="applicable_period"
                title="Applicable Period"
                dateFormat="MM-DD-yyyy"
                timeFormat={false}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register("applicable_period")}
                onDateChange={setApplicablePeriod}
                value={applicablePeriod}
                initialValue={applicablePeriod}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <ReactSelect
                id="quarter-dropdown"
                name="quarter"
                title="Quarter"
                options={quarters}
                control={control}
                register={register("quarter")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <Textarea
                id="address"
                name="address"
                title="Address"
                register={register("address")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-2 pt-2">
            <div className="col-span-2">
              <button
                type="submit"
                className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
                {!isLoading && <IoSaveOutline className="mr-3" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreditableForm;
